import React, { useEffect, useMemo, useState } from "react";
import {
  fetchPTORequestsForWorker,
  fetchPTORequestsForWorkerAdmin,
} from "@service/ptoService";
import HeadlessDialog from "@components/Dialog";
import { LuCalendarDays } from "react-icons/lu";
import { useTranslation } from "react-i18next";

interface PTORequest {
  id: number;
  start_date: string;
  end_date: string;
  hours_requested: number;
  status: string;
  request_type?: string;
  pto_balance_at_request?: number;
  pto_balance_after?: number;
  created_at: string;
  status_reason?: string | null;
}

interface PTORequestsTableProps {
  workerId: number;
  requestType?: "vacation" | "sick" | "all";
  isOpen: boolean;
  onClose: () => void;
  isAdminView?: boolean;
}

export const PTORequestsTable: React.FC<PTORequestsTableProps> = ({
  workerId,
  requestType = "all",
  isOpen,
  onClose,
  isAdminView = false,
}) => {
  const { t } = useTranslation();
  const breakpoint = 634; // Define the breakpoint for mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  const startYearFilter = useMemo(() => new Date().getFullYear() - 1, []);
  const [requests, setRequests] = useState<PTORequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakpoint);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  useEffect(() => {
    if (isOpen && workerId) {
      setLoading(true);
      setError(null);

      const fetchFunction = isAdminView
        ? fetchPTORequestsForWorkerAdmin(workerId)
        : fetchPTORequestsForWorker(workerId, startYearFilter);

      fetchFunction
        .then((data) => {
          let filteredRequests = data.requests || [];

          // Filter by request type if specified
          if (requestType !== "all") {
            filteredRequests = filteredRequests.filter(
              (req: PTORequest) => req.request_type === requestType
            );
          }

          setRequests(filteredRequests);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to fetch PTO requests:", err);
          setError(err.message || t("ptoRequestsTable.errorLoading"));
          setLoading(false);
        });
    }
  }, [isOpen, workerId, requestType, isAdminView, t, startYearFilter]);

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      Approved: "tw-bg-green-100 tw-text-green-800",
      Pending: "tw-bg-yellow-100 tw-text-yellow-800",
      Rejected: "tw-bg-red-100 tw-text-red-800",
      Balance_Overridden: "tw-bg-green-100 tw-text-green-800",
    };

    return (
      <span
        className={`tw-text-center tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${
          statusColors[status] || "tw-bg-gray-100 tw-text-gray-800"
        }`}
      >
        {status.replace("_", " ")}
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const getTitle = () => {
    if (requestType === "vacation")
      return t("ptoRequestsTable.vacationRequests");
    if (requestType === "sick") return t("ptoRequestsTable.sickRequests");
    return t("ptoRequestsTable.ptoRequests");
  };

  return (
    <HeadlessDialog
      title={
        <div className="tw-flex tw-items-center tw-gap-2">
          <LuCalendarDays className="tw-text-primary-light" size={24} />
          <span>{getTitle()}</span>
        </div>
      }
      description=""
      isOpen={isOpen}
      setIsOpen={onClose}
      onClose={onClose}
      buttonText={t("ptoRequestsTable.close")}
      size="tw-w-[100vw] md:tw-max-w-[70vw]"
    >
      <div className="tw-px-2 sm:tw-px-6 tw-pb-2 sm:tw-pb-6">
        {loading && (
          <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
            <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
          </div>
        )}

        {error && (
          <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-4 tw-mb-4">
            <p className="tw-text-red-700 tw-text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && requests.length === 0 && (
          <div className="tw-text-center tw-py-8 tw-text-gray-500">
            {t("ptoRequestsTable.noRequests")}
          </div>
        )}

        {!loading && !error && requests.length > 0 && (
          <div className="tw-flex tw-flex-col tw-gap-4">
            {isMobile && (
              <div className="tw-space-y-3">
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-shadow-sm"
                  >
                    <div className="tw-flex tw-justify-between tw-items-start tw-gap-4">
                      <div>
                        <p className="tw-text-xs tw-font-semibold tw-text-gray-900">
                          {formatDate(request.start_date)} •{" "}
                          {formatDate(request.end_date)}
                        </p>
                        <p className="tw-text-[11px] tw-text-gray-600 tw-capitalize">
                          {request.request_type || "—"}
                        </p>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>
                    <div className="tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-3">
                      <div>
                        <p className="tw-text-[10px] tw-uppercase tw-text-gray-500">
                          {t("ptoRequestsTable.hours")}
                        </p>
                        <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                          {request.hours_requested}h
                        </p>
                      </div>
                      <div>
                        <p className="tw-text-[10px] tw-uppercase tw-text-gray-500">
                          {t("ptoRequestsTable.balanceBefore")}
                        </p>
                        <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                          {request.pto_balance_at_request !== null &&
                          request.pto_balance_at_request !== undefined
                            ? `${request.pto_balance_at_request}h`
                            : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="tw-text-[10px] tw-uppercase tw-text-gray-500">
                          {t("ptoRequestsTable.balanceAfter")}
                        </p>
                        <p className="tw-text-sm tw-font-semibold tw-text-gray-900">
                          {request.pto_balance_after !== null &&
                          request.pto_balance_after !== undefined
                            ? `${request.pto_balance_after}h`
                            : "—"}
                        </p>
                      </div>
                      {request.status_reason && (
                        <div className="tw-col-span-2">
                          <p className="tw-text-[10px] tw-uppercase tw-text-gray-500">
                            {t("ptoRequestsTable.statusReason")}
                          </p>
                          <p className="tw-text-sm tw-text-gray-700">
                            {request.status_reason}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isMobile && (
              <div className="tw-overflow-x-auto tw-rounded-xl tw-border tw-border-gray-200 tw--mx-2 sm:tw-mx-0">
                <div className="tw-inline-block tw-min-w-full tw-align-middle">
                  <table
                    className="tw-min-w-full tw-divide-y tw-divide-gray-200"
                    style={{ minWidth: "600px" }}
                  >
                    <thead>
                      <tr className="tw-bg-[#2642c9]">
                        <th className="tw-bg-[#2642c9] tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-left tw-text-[10px] sm:tw-text-xs tw-font-medium tw-text-white tw-uppercase tw-tracking-wider">
                          {t("ptoRequestsTable.startDate")}
                        </th>
                        <th className="tw-bg-[#2642c9] tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-left tw-text-[10px] sm:tw-text-xs tw-font-medium tw-text-white tw-uppercase tw-tracking-wider">
                          {t("ptoRequestsTable.endDate")}
                        </th>
                        <th className="tw-bg-[#2642c9] tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-left tw-text-[10px] sm:tw-text-xs tw-font-medium tw-text-white tw-uppercase tw-tracking-wider">
                          {t("ptoRequestsTable.hours")}
                        </th>
                        <th className="tw-bg-[#2642c9] tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-left tw-text-[10px] sm:tw-text-xs tw-font-medium tw-text-white tw-uppercase tw-tracking-wider">
                          {t("ptoRequestsTable.type")}
                        </th>
                        <th className="tw-bg-[#2642c9] tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-left tw-text-[10px] sm:tw-text-xs tw-font-medium tw-text-white tw-uppercase tw-tracking-wider">
                          {t("ptoRequestsTable.status")}
                        </th>
                        <th className="tw-bg-[#2642c9] tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-left tw-text-[10px] sm:tw-text-xs tw-font-medium tw-text-white tw-uppercase tw-tracking-wider">
                          {t("ptoRequestsTable.balanceBefore")}
                        </th>
                        <th className="tw-bg-[#2642c9] tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-left tw-text-[10px] sm:tw-text-xs tw-font-medium tw-text-white tw-uppercase tw-tracking-wider">
                          {t("ptoRequestsTable.balanceAfter")}
                        </th>
                        <th className="tw-bg-[#2642c9] tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-left tw-text-[10px] sm:tw-text-xs tw-font-medium tw-text-white tw-uppercase tw-tracking-wider">
                          {t("ptoRequestsTable.statusReason")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
                      {requests.map((request) => (
                        <tr key={request.id} className="hover:tw-bg-gray-50">
                          <td className="tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-whitespace-nowrap tw-text-[10px] sm:tw-text-sm tw-text-gray-900">
                            {formatDate(request.start_date)}
                          </td>
                          <td className="tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-whitespace-nowrap tw-text-[10px] sm:tw-text-sm tw-text-gray-900">
                            {formatDate(request.end_date)}
                          </td>
                          <td className="tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-whitespace-nowrap tw-text-[10px] sm:tw-text-sm tw-text-gray-900">
                            {request.hours_requested}h
                          </td>
                          <td className="tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-whitespace-nowrap tw-text-[10px] sm:tw-text-sm tw-text-gray-600 tw-capitalize">
                            {request.request_type || "—"}
                          </td>
                          <td className="tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-whitespace-nowrap">
                            {getStatusBadge(request.status)}
                          </td>
                          <td className="tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-whitespace-nowrap tw-text-[10px] sm:tw-text-sm tw-text-gray-900">
                            {request.pto_balance_at_request !== null &&
                            request.pto_balance_at_request !== undefined
                              ? `${request.pto_balance_at_request}h`
                              : "—"}
                          </td>
                          <td className="tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-whitespace-nowrap tw-text-[10px] sm:tw-text-sm tw-text-gray-900">
                            {request.pto_balance_after !== null &&
                            request.pto_balance_after !== undefined
                              ? `${request.pto_balance_after}h`
                              : "—"}
                          </td>
                          <td className="tw-px-2 sm:tw-px-4 tw-py-2 sm:tw-py-3 tw-text-[10px] sm:tw-text-sm tw-text-gray-700">
                            {request.status_reason &&
                            request.status_reason.trim().length > 0
                              ? request.status_reason
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="tw-mt-2 sm:tw-mt-4 tw-text-[10px] sm:tw-text-xs tw-text-gray-500 tw-border-t tw-pt-2 sm:tw-pt-4">
          <p>
            {t("ptoRequestsTable.totalRequests")}: {requests.length}
          </p>
        </div>
      </div>
    </HeadlessDialog>
  );
};

export default PTORequestsTable;
